import { Lead, LeadState } from '@/api/types/crm.types'
import { FC, useEffect, useState } from 'react'
import mclasses from '../../styles/modal.module.css'
import classes from './index.module.css'
import cn from 'classnames'
import Modal from '@common/Modal'
import { crmApi } from '@/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import { IcoRemove } from '@/utils/icons'
import Button from '@UI/New/Button'
import { isError } from '@/helpers/checkQuery'
import { getInArr } from '@/helpers/getInArr'
import { getLeadFieldValue } from '../../utils/leadFields'
import { hasManagerForAllLeads } from './utils'
import { useSelector } from 'react-redux'
import { selectProfile } from '@store/profile.reducer'
import { useAsync } from 'react-use'
import Select from '../base/Select/Select'

interface ChangeLeadStateModalProps {
  newType?: LeadState
  changeManager?: boolean
  userRole: string
  lead?: Lead | Lead[]
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

const ChangeLeadStateModal: FC<ChangeLeadStateModalProps> = ({
  newType,
  changeManager,
  userRole,
  lead,
  open,
  onClose,
  onSuccess,
}) => {
  const addAlert = useAddAlert()
  const userid = useSelector(selectProfile)?.id
  const isManager = userRole === 'sales-manager'

  const managersState = useAsync(crmApi.getAllManagers, [])
  const managers = managersState.value?.data?.data || []

  useEffect(() => {
    if (managersState.error) {
      addAlert({
        title: 'Ошибка',
        text: 'Не удалось загрузить список менеджеров',
        type: 'error',
      })
    }
  }, [managersState.error])

  const [title, setTitle] = useState('')
  const [managerId, setManagerId] = useState<number | null>(null)
  const [needSelectManager, setNeedSelectManager] = useState(false)

  const [error, setError] = useState(false)

  useEffect(() => {
    if (changeManager) setNeedSelectManager(true)
    else setNeedSelectManager(false)
    setError(false)

    const currentLeadType = Array.isArray(lead) ? lead?.[0]?.type : lead?.type

    // if (!hasManagerForAllLeads(lead)) {

    // }

    if (newType === 'in_work' && !isManager) {
      Array.isArray(lead)
        ? setTitle('Кому передать лидов?')
        : setTitle('Кому передать лида?')
      setNeedSelectManager(true)
      return
    }

    if (newType === 'in_work' && isManager) {
      Array.isArray(lead)
        ? setTitle('Вы уверены что хотите взять лидов?')
        : setTitle('Вы уверены что хотите взять лида?')
      userid
        ? setManagerId(managers.find((m) => m.user.id === userid)?.id ?? null)
        : setManagerId(null)
      return
    }

    if (newType === 'processed') {
      Array.isArray(lead)
        ? setTitle('Вы уверены что хотите перевести лидов в обработанные?')
        : setTitle('Вы уверены что хотите перевести лида в обработанные?')
      return
    }

    if (newType === 'client') {
      Array.isArray(lead)
        ? setTitle('Вы уверены что хотите перевести лидов в клиенты?')
        : setTitle('Вы уверены что хотите перевести лида в клиенты?')
      return
    }

    if (newType === undefined || newType === 'new') {
      setTitle('Вы не можете совершить это действие')
      setError(true)
      return
    }

    setTitle('Ошибка')
    setError(true)
  }, [newType, userRole, lead, changeManager])

  if (open && (lead === undefined || (Array.isArray(lead) && lead.length === 0))) {
    onClose()
  }

  const handleConfirm = () => {
    if (lead === undefined || (Array.isArray(lead) && lead.length === 0)) {
      onClose()
      return
    }

    if (needSelectManager && !managerId) {
      addAlert({
        title: 'Ошибка',
        text: 'Выберите менеджера',
        type: 'error',
      })
      return
    }

    crmApi
      .changeLeadsState(
        { type: newType!, manager_id: managerId ?? undefined },
        { lead_ids: Array.isArray(lead) ? lead.map((l) => l.id) : [lead.id] },
      )
      .then((res) => {
        if (isError(res)) throw new Error()
        addAlert({
          title: 'Успешно',
          text: `Успешно изменено`,
          type: 'success',
        })
        onSuccess && onSuccess()
      })
      .catch((error) => {
        addAlert({
          title: 'Ошибка',
          text: 'Ошибка',
          type: 'error',
        })
        onClose()
      })
  }

  const getLeadNames = () => {
    if (!Array.isArray(lead)) return getLeadFieldValue(lead, 'NAME')
    return lead?.map((lead) => getLeadFieldValue(lead, 'NAME')).join(', ')
  }

  return (
    <Modal
      active={open}
      className={mclasses.modalWrapper as any}
      setActive={() => onClose()}
    >
      <div className={mclasses.modalContainer}>
        <div className={mclasses.modalHeader}>
          <div className={mclasses.modalTitleContainer}>
            <span className={mclasses.modalHeaderTitle}>{title}</span>
          </div>
          <div className={mclasses.closeIcon} onClick={() => onClose()}>
            <IcoRemove />
          </div>
        </div>
        <div className={cn(mclasses.formWrapper, classes.formWrapper)}>
          <div>
            <p className={mclasses.label}>
              {Array.isArray(lead) ? 'Лиды' : 'Фио лида'}
            </p>
            <p>{getLeadNames()}</p>
          </div>
          {needSelectManager && (
            <div>
              <p className={mclasses.label}>Фио менеджера</p>
              <Select
                showSearch
                placeholder="Выберите менеджера"
                onChange={(value) => setManagerId(value)}
                options={managers.map((manager) => ({
                  value: manager.id,
                  label: manager.fio,
                }))}
                loading={managersState.loading}
              />
            </div>
          )}
        </div>
        <div className={mclasses.actions}>
          <Button color="gray" onClick={() => onClose()}>
            Отменить
          </Button>
          <Button
            color="primary"
            onClick={() => handleConfirm()}
            disabled={error}
          >
            Подтвердить
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default ChangeLeadStateModal
